<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard
            class="max-h-[calc(100vh-80px)]"
            :ui="{
                rounded: 'rounded-lg',
                body: { base: transferEnabled ? 'overflow-auto' : '' }
            }"
        >
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">
                        Delete Customer
                    </h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div class="space-y-4">
                <UAlert
                    title="This action is non-reversible and destructive!"
                    description="Any subscriptions will be permanently deleted."
                    icon="i-ph-warning-duotone"
                    color="red"
                    variant="subtle"
                />

                <UCheckbox v-model="transferEnabled" label="Transfer calls, reports & transports to another customer" />

                <template v-if="transferEnabled">
                    <UInput
                        v-model="search"
                        :loading="searching"
                        size="lg"
                        placeholder="Search by name or phone..."
                        icon="i-ph-magnifying-glass-duotone"
                    />

                    <div v-if="selectedCustomer" class="flex items-center justify-between rounded-md border border-primary-500 bg-primary-50 px-4 py-3">
                        <div class="flex items-center space-x-3">
                            <Avatar
                                size="sm"
                                :path="selectedCustomer.avatar"
                                :placeholder="[selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ')"
                            />
                            <div class="leading-4">
                                <p class="text-sm font-medium">{{ [selectedCustomer.first_name, selectedCustomer.middle_name, selectedCustomer.last_name].filter(Boolean).join(' ') }}</p>
                                <span class="text-xs text-gray-500">{{ selectedCustomer.phone || selectedCustomer.email }}</span>
                            </div>
                        </div>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" size="xs" @click="selectedCustomer = undefined" />
                    </div>

                    <div v-else-if="search && !searching" class="max-h-60 overflow-auto">
                        <div v-if="!customers.length" class="py-6 text-center">
                            <UIcon name="i-ph-magnifying-glass-minus-duotone" class="mx-auto size-6 text-gray-400" />
                            <p class="mt-1 text-sm text-gray-500">No results found.</p>
                        </div>
                        <div v-else>
                            <button
                                v-for="c in customers"
                                :key="c.id"
                                type="button"
                                @click="selectedCustomer = c"
                                class="-mt-px relative w-full px-4 py-3 border first:rounded-t-md last:rounded-b-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary transition-colors"
                            >
                                <div class="flex items-center space-x-3">
                                    <Avatar
                                        size="sm"
                                        :path="c.avatar"
                                        :placeholder="[c.first_name, c.last_name].filter(Boolean).join(' ')"
                                    />
                                    <div class="flex-1 text-left leading-4">
                                        <p class="text-sm font-medium">{{ [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(' ') }}</p>
                                        <span class="text-xs text-gray-500">{{ [c.phone, c.email].filter(Boolean).join(' · ') }}</span>
                                    </div>
                                    <UIcon name="i-ph-caret-right-bold" class="size-3 text-gray-400" />
                                </div>
                            </button>
                        </div>
                    </div>
                </template>

                <p>Are you sure you want to delete this customer?</p>
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" size="lg" color="white">
                        Cancel
                    </UButton>
                    <UButton
                        @click="confirm()"
                        size="lg"
                        :loading="confirming"
                        :disabled="transferEnabled && !selectedCustomer"
                        color="red"
                    >
                        Delete
                    </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { Customer } from '~/types/customer'
import { UserRole } from '~/types/user'

const emit = defineEmits<{
    'update:open': [boolean]
    'confirmed': [void]
}>()

const props = defineProps<{
    open: boolean
    customer?: Customer
}>()

const toast = useToast()
const auth = useAuthStore()
const client = useSupabaseClient()

const confirming = ref(false)
const transferEnabled = ref(false)
const search = ref('')
const searching = ref(false)
const searchTimeout = ref<NodeJS.Timeout>()
const customers = ref<Customer[]>([])
const selectedCustomer = ref<Customer>()

watch(() => props.open, (open) => {
    if (open) {
        transferEnabled.value = false
        search.value = ''
        customers.value = []
        selectedCustomer.value = undefined
    }
})

watch(search, (value) => {
    if (value) {
        fetchCustomers()
    } else {
        customers.value = []
    }
})

function fetchCustomers() {
    searching.value = true

    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
    }

    searchTimeout.value = setTimeout(async () => {
        const searchQuery = search.value.trim()
        const numberQuery = parseInt(search.value.replace(/[^\d]/g, ''))

        const { data } = await client.from('customers')
            .select('id, number, first_name, middle_name, last_name, email, phone, avatar')
            .neq('id', props.customer?.id)
            .or([
                `fts.plfts(simple).'${searchQuery}'`,
                `email.ilike.%${searchQuery}%`,
                !isNaN(numberQuery) ? `phone.ilike.%${numberQuery}` : undefined,
                !isNaN(numberQuery) && numberQuery <= 2147483647 ? `number.eq.${numberQuery}` : undefined
            ].filter(Boolean).join(','))

        if (data) {
            customers.value = data as Customer[]
        }

        searching.value = false
    }, 500)
}

async function confirm() {
    if (!auth.hasRole(UserRole.admin)) {
        toast.add({
            title: 'You do not have permission to delete customers',
            color: 'red',
            icon: 'i-ph-warning-circle-duotone'
        })

        return dismiss()
    }

    if (props.customer?.id) {
        confirming.value = true

        try {
            const query = selectedCustomer.value
                ? `?transferTo=${selectedCustomer.value.id}`
                : ''

            await $fetch(`/api/customers/${props.customer.id}${query}`, {
                method: 'DELETE'
            })

            toast.add({
                title: 'Customer deleted successfully!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })

            emit('confirmed')
        } catch (error: any) {
            toast.add({
                title: 'Could not delete customer!',
                description: error?.data?.message || 'Please try again later.',
                color: 'red',
                icon: 'i-ph-x-circle-duotone'
            })
        } finally {
            confirming.value = false
            dismiss()
        }
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>
